import { useEffect, useState } from 'react';
import { defaultDestination, formatDestination, isSameDestination, parseDestinationInput } from '../../webhook/destination';

const INPUT_ERROR = 'Use one customer attribute, like {customer.phone}, or a phone number with its country code.';

// One box, in the message fields' own token syntax: {customer.<attribute>} sends to each customer, a phone
// number sends every message to that one number (for testing). onChange only ever gets a valid, normalized
// destination; text being typed stays local until blur or Enter.
const SendToControl = ({ value, defaultAttribute, onChange }) => {
  const defaultDest = defaultDestination(defaultAttribute);
  const [text, setText] = useState(() => formatDestination(value));
  const [error, setError] = useState('');

  // Follows the committed destination (a restore, Reset).
  useEffect(() => {
    setText(formatDestination({ type: value.type, value: value.value }));
    setError('');
  }, [value.type, value.value]);

  const commit = () => {
    const parsed = parseDestinationInput(text);
    if (!parsed) {
      setError(INPUT_ERROR);
      return;
    }
    setError('');
    setText(formatDestination(parsed));
    if (!isSameDestination(parsed, value)) onChange(parsed);
  };

  const reset = () => {
    setError('');
    setText(formatDestination(defaultDest));
    onChange(defaultDest);
  };

  return (
    <div className="br-sendto" data-testid="bloomreach-sendto">
      <label className="br-sendto__label" htmlFor="br-sendto-input">
        Send to
      </label>
      <input
        id="br-sendto-input"
        type="text"
        className={`br-sendto__input${error ? ' br-sendto__input--invalid' : ''}`}
        data-testid="bloomreach-sendto-input"
        placeholder={formatDestination(defaultDest)}
        spellCheck={false}
        autoComplete="off"
        aria-invalid={!!error}
        aria-describedby="br-sendto-help"
        value={text}
        onChange={(event) => setText(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          commit();
        }}
      />
      {!isSameDestination(value, defaultDest) && (
        <button
          type="button"
          className="br-sendto__reset"
          data-testid="bloomreach-sendto-reset"
          title={`Reset to ${formatDestination(defaultDest)}`}
          onClick={reset}
        >
          Reset
        </button>
      )}
      <div id="br-sendto-help" className="br-sendto__help">
        {error && (
          <p className="br-sendto__error" role="alert" data-testid="bloomreach-sendto-error">
            {error}
          </p>
        )}
        {value.type === 'number' ? (
          <p className="br-sendto__warn" data-testid="bloomreach-sendto-test-warning">
            Test number: every send from this message goes to {value.value}, not to customers. Reset and save before the
            scenario goes live.
          </p>
        ) : (
          <p className="br-sendto__hint">
            Sends to each customer's <code>{value.value}</code> attribute. To test, type a phone number with its
            country code.
          </p>
        )}
      </div>
    </div>
  );
};

export default SendToControl;
